import React, { useState } from 'react';
import { Phone, Clock, MapPin, Stethoscope, Menu, X, ChevronRight, ChevronDown, Calendar, Mail, ShieldPlus, Baby, HeartPulse, Printer, Activity, Bed, Users, HeartHandshake, Image as ImageIcon, Loader2, Car, ClipboardList, ArrowRight, ArrowLeft, Home, FileText, CheckCircle2, Footprints, ExternalLink, Building, Dumbbell, UserCheck, Smile, BookOpen, Utensils } from 'lucide-react';

// --- データ定義（ここにお知らせを追加していきます） ---
const NEWS_DATA = [
  {
    id: 9,
    date: '2026.10.23',
    category: 'インフルエンザ',
    categoryColor: 'bg-blue-100 text-blue-700',
    title: 'インフルエンザ予防接種を開始しました',
    content: (
      <div className="space-y-4">
        <p>今年度のインフルエンザ予防接種を開始いたしました。</p>
        <p>
          当院は予約制ではありませんので、接種をご希望の方は、
          お電話にてお問い合わせいただくか、直接当院受付までお越しください。
        </p>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
          <p className="font-bold text-slate-700 mb-2">【接種期間：1月末まで】</p>
          <ul className="list-disc list-inside space-y-1 text-slate-700">
            <li>65歳未満：4,000円（税込）</li>
            <li>65〜69歳：2,250円（税込）</li>
            <li>70歳以上：1,200円（税込）</li>
          </ul>
        </div>
      </div>
    )
  },

  {
    id: 8,
    date: '2026.10.23',
    category: '重要',
    categoryColor: 'bg-red-100 text-red-700',
    title: '10月27日（火）午後の診療（休診）について',
    content: (
      <div className="space-y-4">
        <p>10月27日（火）午後の診療は以下の通り変更させていただきます。</p>

        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
          <p className="font-bold text-slate-700 mb-2">【10月27日（火）の診療体制】</p>
          <ul className="list-disc list-inside space-y-2">
            <li><span className="font-bold">午前（9:30〜12:00）</span>：通常通り診療</li>
            <li>
              <span className="font-bold text-red-600">午後（15:30〜18:30）</span>：
              <span className="font-bold text-red-600">外来休診（手術のため）</span>
            </li>
          </ul>
        </div>

        <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex items-start gap-3">
          <Activity className="text-red-600 shrink-0 mt-1" size={20} />
          <p className="text-red-800 text-sm font-bold">
            ※今回は、午後のリハビリテーションもお休みとさせていただきます。
          </p>
        </div>

        <p>ご迷惑をおかけいたしますが、何卒ご理解のほどお願い申し上げます。</p>
      </div>
    )
  },

  {
    id: 6,
    date: '2026.07.19',
    category: 'お知らせ',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    title: '新しいリハビリ機器を2台導入しました',
    linkToBlogId: 4,
    content: (
      <div className="space-y-4">
        <p>このたび原田整形外科では、リハビリテーションをより充実させるため、新しい治療機器を2台（<span className="font-bold">カイネタイザー KT-108＋</span>・<span className="font-bold">マイクロタイザー MT-5DLL</span>）を導入しました。</p>
        <p>腰・肩・膝・足首などの痛みやこわばりでお困りの方への、リハビリのお手伝いに活用してまいります。</p>
      </div>
    )
  },

  {
    id: 3,
    date: '2025.09.01',
    category: '重要',
    categoryColor: 'bg-red-100 text-red-700',
    title: 'マイナンバーカードの保険証利用について',
    content: (
      <div className="space-y-4">
        <p>当院では、マイナンバーカードを健康保険証として利用できる「マイナ受付」に対応しております。</p>
        <p>マイナンバーカードをご利用いただくことで、過去の特定健診結果や薬剤情報を閲覧可能となり、より適切な医療の提供につなげることができます。</p>
        <p>ご来院の際は、ぜひマイナンバーカードをご持参ください。</p>
      </div>
    )
  }
];

// --- ブログ記事データ（ここに読み物記事を追加していきます。お知らせと同じ書き方です） ---
// 下書き（未公開）の記事置き場。公開するときは BLOG_DATA の先頭に移す
// eslint-disable-next-line no-unused-vars
const BLOG_DRAFTS = [
  {
    id: 6,
    date: '2026.07.29',
    category: '当院の取り組み',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    title: '骨粗しょう症の治療、効果はどう確かめる？ ― 血液検査でみる骨代謝の変化',
    excerpt: '骨粗しょう症の治療は、痛みなどの自覚症状がないまま何年も続けるものです。当院で行っている血液検査（骨代謝マーカー）で、骨の変化を確認する方法についてご紹介します。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          骨粗しょう症の治療は、多くの場合、<span className="font-bold text-slate-800">痛みなどの自覚症状がないまま</span>、何年も続けていくものです。「この治療は、本当に効いているのだろうか」。そう感じたことはありませんか。
        </p>
        <p>
          実は、骨密度の検査（DXA法）だけでなく、<span className="font-bold text-slate-800">血液検査</span>によっても、骨の状態の変化を確認することができます。当院で行っている「骨代謝マーカー」の検査についてご紹介します。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">血液検査で何がわかるのか</h2>
        <p>
          私たちの骨は、古い骨が壊される「骨吸収」と、新しい骨が作られる「骨形成」をくり返しながら、少しずつ生まれ変わっています。骨代謝マーカーは、この<span className="font-bold text-slate-800">骨形成や骨吸収がどのくらい活発に行われているか</span>を、血液を通じて調べる検査です。
        </p>

        {/* 図：P1NP（骨形成）とTRACP-5b（骨吸収）のカード */}
        <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-sky-700 bg-sky-100 rounded-full px-3 py-0.5 mb-2">骨形成の目安</span>
            <p className="font-bold text-slate-800">P1NP（ピーワンエヌピー）</p>
            <p className="text-sm text-slate-500 mt-1">新しい骨が作られる活動の程度を示します</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-amber-700 bg-amber-100 rounded-full px-3 py-0.5 mb-2">骨吸収の目安</span>
            <p className="font-bold text-slate-800">TRACP-5b（トラップ・ファイブビー）</p>
            <p className="text-sm text-slate-500 mt-1">古い骨が壊される活動の程度を示します</p>
          </div>
        </div>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">なぜこの検査が大切なのか</h2>
        <p>
          骨密度検査（DXA法）で治療による変化をはっきりと捉えるには、<span className="font-bold text-slate-800">1〜2年ほど</span>かかることがあります。一方、骨代謝マーカーは、薬の種類によっては治療開始後の比較的早い段階から変化がみられます。こうした変化は、<span className="font-bold text-emerald-700">薬が体の中で作用しているかを確認する手がかり</span>になります。
        </p>
        <p>
          たとえば、骨吸収を抑える薬では骨吸収マーカー（TRACP-5bなど）が低下し、骨形成を促す薬ではP1NPが上昇するなど、薬によって変化の仕方は異なります。
        </p>
        <div className="bg-sky-50 border border-sky-100 rounded-xl p-5 text-sm text-slate-600">
          TRACP-5bは、食事の影響を受けにくく、採血の時間帯を気にする必要が少ない検査ともされています。早い段階で治療の手応えを確認できることは、治療を続けていくうえでの安心材料にもつながります。
        </div>
        <p className="text-sm text-slate-500">
          ※ 骨代謝マーカーの値だけで、治療効果のすべてがわかるわけではありません。骨密度検査や骨折の有無、服薬状況などとあわせて、医師が総合的に判断します。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">当院での検査について</h2>
        <p>
          この検査は、すべての患者さんに一律に行っているものではありません。骨粗しょう症の治療を行う中で、<span className="font-bold text-slate-800">患者さんの状態に応じて、医師が必要と判断した場合</span>に実施しています。検査についてもっと知りたい方は、診察の際にご相談ください。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">今日からできること</h2>
        <p>
          骨粗しょう症の治療は、目に見える変化を感じにくいからこそ、続けることが難しく感じられることもあります。<span className="font-bold text-slate-800">治療の効果が気になる方は、遠慮なく診察時にご相談ください</span>。そして、自己判断で治療を中断せず、医師と相談しながら続けていただくことが、これからの骨の健康を守ることにつながります。
        </p>

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 flex items-start gap-3">
          <BookOpen className="text-emerald-600 shrink-0 mt-0.5" size={22} />
          <p className="text-emerald-900">
            この記事は、骨粗しょう症の<span className="font-bold">「治療を続ける中」</span>でのお話です。「そもそも骨の状態を調べてみたい」という方は、
            <span className="font-bold">「骨粗しょう症は、骨折するまで気づきにくい」</span>の記事もあわせてご覧ください。
          </p>
        </div>

        <div className="mt-8 pt-4 border-t border-slate-100 text-sm text-slate-500">
          <p className="font-bold mb-1">参考資料</p>
          <ul className="space-y-1">
            <li>骨粗鬆症の予防と治療ガイドライン2025年版</li>
            <li>日本骨粗鬆症学会 骨代謝マーカー検討委員会「骨粗鬆症診療における骨代謝マーカーの適正使用ガイド2018年版」</li>
            <li>亀田メディカルセンター 骨粗鬆症リエゾンチーム「骨代謝マーカーについて」</li>
            <li>株式会社保健科学研究所「骨粗鬆症における骨代謝マーカーの利用方法について」</li>
          </ul>
        </div>
      </div>
    )
  },
];

const BLOG_DATA = [
  {
    id: 8,
    date: '2026.08.13',
    category: '季節の健康メモ',
    categoryColor: 'bg-amber-100 text-amber-700',
    title: '9月も油断できません ― 高齢の方の脱水・熱中症にご用心',
    excerpt: '熱中症は、真夏だけのものではありません。高齢の方は、のどの渇きに気づきにくく、家の中でも脱水が進みやすいもの。脱水は、ふらつき・転倒のもとにもなります。今日からできる水分のとり方をまとめました。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          厳しい暑さが続いています。熱中症というと真夏のイメージがありますが、実は<span className="font-bold text-slate-800">9月に入っても、熱中症で救急搬送される方は少なくありません</span>。
        </p>
        <p>
          そして、熱中症で搬送される方の<span className="font-bold text-slate-800">半数以上は、65歳以上の高齢の方</span>とされています。発生場所として最も多いのは、炎天下の屋外ではなく、住み慣れた<span className="font-bold text-slate-800">家の中</span>です。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-amber-400 pl-3 mb-2">なぜ高齢になると、脱水になりやすいのか</h2>
        <p>
          年齢を重ねると、体は少しずつ、水分をためにくく・失いやすくなっていきます。理由は、主に3つあります。
        </p>

        {/* 図：脱水になりやすい3つの理由 */}
        <div className="my-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 text-center">
            <span className="inline-flex w-9 h-9 rounded-full bg-amber-400 text-white font-bold items-center justify-center mb-2">1</span>
            <p className="font-bold text-slate-800">体の水分が、もともと少ない</p>
            <p className="text-sm text-slate-500 mt-1">体にたくわえられる水分の量は、年齢とともに減っていきます</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 text-center">
            <span className="inline-flex w-9 h-9 rounded-full bg-amber-400 text-white font-bold items-center justify-center mb-2">2</span>
            <p className="font-bold text-slate-800">のどの渇きに気づきにくい</p>
            <p className="text-sm text-slate-500 mt-1">渇きを感じる働きが、年齢とともに鈍くなります</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 text-center">
            <span className="inline-flex w-9 h-9 rounded-full bg-amber-400 text-white font-bold items-center justify-center mb-2">3</span>
            <p className="font-bold text-slate-800">水分を控えてしまいがち</p>
            <p className="text-sm text-slate-500 mt-1">トイレが近くなるのを気にして、飲む量を減らしてしまう方もいます</p>
          </div>
        </div>
        <p>
          のどが渇いたと感じたときには、すでに脱水が始まっていることもあります。合言葉は<span className="font-bold text-amber-700">「渇く前に、飲む」</span>です。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-amber-400 pl-3 mb-2">脱水は、転倒・骨折にもつながります</h2>
        <p>
          脱水がこわいのは、熱中症だけではありません。体の水分が減ると、<span className="font-bold text-slate-800">めまい・立ちくらみ・ふらつき</span>が起こりやすくなります。
        </p>
        <p>
          脱水によるめまいやふらつきは、転倒の一因になります。高齢の方の転倒は骨折につながりやすいため、<span className="font-bold text-slate-800">こまめな水分補給は、転倒・骨折のリスクを減らすうえでも大切</span>なのです。
        </p>
        <p className="text-sm text-slate-500">
          ※ 夜中に足がつる「こむら返り」も、水分や塩分のバランスと関連があると考えられています。ただし、こむら返りには脱水以外にもさまざまな原因があります。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-amber-400 pl-3 mb-2">今日からできること</h2>
        <ul className="space-y-3">
          <li className="flex gap-3">
            <span className="text-amber-500 font-bold shrink-0">●</span>
            <span><span className="font-bold text-slate-800">時間を決めて、こまめに飲む。</span>起きたとき・お風呂の前後・寝る前・外出の前後は、のどが渇いていなくても、少しずつ飲みましょう。</span>
          </li>
          <li className="flex gap-3">
            <span className="text-amber-500 font-bold shrink-0">●</span>
            <span><span className="font-bold text-slate-800">量の一般的な目安は、食事のほかに1日1.2リットルほど（コップ約6杯）。</span>必要な量は人によって違うため、あくまで目安です。</span>
          </li>
          <li className="flex gap-3">
            <span className="text-amber-500 font-bold shrink-0">●</span>
            <span><span className="font-bold text-slate-800">エアコンを我慢しない。</span>室温の目安は28℃（エアコンの設定温度ではなく、部屋の温度のことです）。夜間も上手に使いましょう。</span>
          </li>
          <li className="flex gap-3">
            <span className="text-amber-500 font-bold shrink-0">●</span>
            <span><span className="font-bold text-slate-800">ふだんの水分補給は、水やお茶で。</span>たくさん汗をかいたときは、適度な塩分も補いましょう。経口補水液（けいこうほすいえき：水分と塩分を体に吸収しやすく調整した飲み物）は、脱水が疑われるときに使うものです。</span>
          </li>
        </ul>
        <div className="bg-sky-50 border border-sky-100 rounded-xl p-5 text-sm text-slate-600">
          心臓や腎臓の病気などで、水分や塩分の制限を受けている方は、自己判断で増やさず、かかりつけ医にご相談ください。
        </div>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-amber-400 pl-3 mb-2">こんなときは、ためらわず受診を</h2>
        <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-rose-50 border border-rose-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-rose-700 bg-rose-100 rounded-full px-3 py-0.5 mb-2">すぐに救急車（119番）を</span>
            <p className="text-sm text-slate-600">呼びかけへの反応がおかしい・意識がない／けいれんしている／自分で水が飲めない／体が非常に熱い</p>
            <p className="text-sm text-slate-600 mt-2">救急車を待つ間も体を冷やしてください。反応がおかしい人に、無理に水を飲ませてはいけません。</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-amber-700 bg-amber-100 rounded-full px-3 py-0.5 mb-2">まずは涼しい場所で休んで</span>
            <p className="text-sm text-slate-600">めまい・立ちくらみ・頭痛・吐き気・だるさ。衣服をゆるめ、首・わきの下・足の付け根を冷やしましょう。意識がはっきりしていて自分で飲める場合は、水分と塩分を。よくならなければ受診を</p>
          </div>
        </div>
        <p>
          軽い症状でも、心配なときは我慢せず、かかりつけ医にご相談ください。暑さの出口まで、もうひと息です。水分と涼しさを味方につけて、残りの夏を無事に乗り切りましょう。
        </p>

        <div className="mt-8 pt-4 border-t border-slate-100 text-sm text-slate-500">
          <p className="font-bold mb-1">参考資料</p>
          <ul className="space-y-1">
            <li>環境省「熱中症環境保健マニュアル」</li>
            <li>厚生労働省「熱中症予防のための情報・資料サイト」</li>
            <li>総務省消防庁「熱中症による救急搬送状況」</li>
            <li>厚生労働省「『健康のため水を飲もう』推進運動」</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    id: 7,
    date: '2026.07.30',
    category: '当院の取り組み',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    title: 'その脚の痛み、原因は腰だけとは限りません ― ABI検査でわかること',
    excerpt: '歩くと脚が痛くなる・しびれる、少し休むと楽になる――そんな症状の原因は、腰だけとは限りません。動脈硬化による血流の問題を調べる、当院のABI検査についてご紹介します。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          「歩いていると脚が痛くなる」「少し休むと、また歩けるようになる」。そんな経験はありませんか。この症状は<span className="font-bold text-slate-800">間欠性跛行（かんけつせいはこう）</span>と呼ばれ、腰の病気で起こることがよく知られています。しかし、原因はそれだけとは限りません。
        </p>
        <p>
          実は、<span className="font-bold text-slate-800">動脈硬化によって脚の血管が狭くなる</span>ことでも、よく似た症状が起こります。当院で行っているABI検査は、この「血管の問題」を調べるための検査です。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">脚の痛みには、代表的な2つの原因が隠れていることがあります</h2>
        <p>
          歩くと脚が痛くなる・しびれる症状には、代表的な原因として次の2つが挙げられます。ひとつは、<span className="font-bold text-slate-800">脊柱管狭窄症</span>など、腰の神経が圧迫されることによるもの。もうひとつは、<span className="font-bold text-slate-800">動脈硬化</span>によって脚の血管が狭くなり、血のめぐりが悪くなることによるものです。このほかにも、関節の病気や神経の障害が原因のことがあり、両方が重なっている場合もあります。
        </p>

        {/* 図：神経性と血管性のカード */}
        <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-sky-700 bg-sky-100 rounded-full px-3 py-0.5 mb-2">神経性を示唆する特徴</span>
            <p className="font-bold text-slate-800">脊柱管狭窄症など</p>
            <p className="text-sm text-slate-500 mt-1">前かがみになると、症状がやわらぐことが多いとされています</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-amber-700 bg-amber-100 rounded-full px-3 py-0.5 mb-2">血管性を示唆する特徴</span>
            <p className="font-bold text-slate-800">動脈硬化による血流の問題</p>
            <p className="text-sm text-slate-500 mt-1">姿勢に関わらず、立ち止まって休むと症状がやわらぐことが多いとされています</p>
          </div>
        </div>
        <p className="text-sm text-slate-500">
          ※ これらはあくまで傾向であり、症状だけで確実に区別できるわけではありません。実際の診断では、問診や脈・皮膚の状態の確認、ABIなどの検査を組み合わせて判断します。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">ABI検査でわかること</h2>
        <p>
          ABI（足関節上腕血圧比）検査は、<span className="font-bold text-slate-800">腕と足首の血圧を測って比べ</span>、脚の動脈が狭くなったり詰まったりしていないかを調べる検査です。血の流れが妨げられていると、足首側の血圧が低く出ます。検査は5〜15分程度で、血圧計で腕と足首を締め付けるため多少の圧迫感はありますが、<span className="font-bold text-slate-800">身体への負担が少ない検査</span>です。
        </p>
        <p className="text-sm text-slate-500">
          ※ ABIの数値が正常でも、血管の病気を完全に否定できるわけではありません。歩いているときだけ血の巡りが不足するタイプもあり、その場合は運動をしてから測り直す検査が検討されることもあります。また、糖尿病や透析治療を受けている方は、血管が硬くなっていることで数値が実際より高く出て、判定が難しくなる場合があります。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">当院での検査について</h2>
        <p>
          この検査は、<span className="font-bold text-slate-800">脚の痛みやしびれを訴える患者さん</span>に対して、状態に応じて行っています。「腰の症状だと思っていたら、実は血管が原因だった」ということもあるため、気になる症状がある方は、診察の際にご相談ください。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">今日からできること</h2>
        <p>
          歩くと脚が痛くなる・しびれる、休むとまた歩けるようになる、といった症状がある方は、<span className="font-bold text-slate-800">自己判断せず、一度ご相談ください</span>。原因によって、必要な治療やケアが変わってきます。
        </p>

        <div className="mt-8 pt-4 border-t border-slate-100 text-sm text-slate-500">
          <p className="font-bold mb-1">参考資料</p>
          <ul className="space-y-1">
            <li>社会福祉法人恩賜財団済生会「間欠性跛行」</li>
            <li>日本循環器学会「2022年改訂版 末梢動脈疾患ガイドライン」</li>
            <li>日本脊髄外科学会「腰部脊柱管狭窄症」</li>
            <li>日本鋼管病院 こうかんクリニック「ABI（足関節上腕血圧比）検査」</li>
            <li>東邦大学医療センター大森病院 臨床生理機能検査部「足関節上腕血圧比(ABI)」</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    id: 5,
    date: '2026.07.22',
    category: '当院の取り組み',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    title: '骨粗しょう症は、骨折するまで気づきにくい ― 症状がないときこそ、骨密度検査を',
    excerpt: '骨がもろくなる「骨粗しょう症」は、初期には自覚症状がほとんどないことが多い病気です。症状がないときこそ、骨密度検査で骨の状態を確認しておくことに意味があります。当院のDXA法による検査についてご紹介します。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          「腰も痛くないし、元気だから大丈夫」。骨のことになると、そう思う方は多いかもしれません。しかし、骨がもろくなり、骨折しやすくなる<span className="font-bold text-slate-800">「骨粗しょう症」は、初期には自覚症状がほとんどないことが多い病気</span>です。
        </p>
        <p>
          痛みや見た目の変化がないまま少しずつ進行し、転倒などをきっかけに<span className="font-bold text-emerald-700">骨折して、初めて骨粗しょう症に気づく</span>こともあります。
        </p>
        <p>
          国内の骨粗しょう症の推定患者数（40歳以上）は約1,590万人（男性約410万人・女性約1,180万人）とされており（日本生活習慣病予防協会）、特に閉経後の女性に多くみられます。ただし、男性も加齢などに伴って骨粗しょう症になることがあります。
        </p>

        {/* 図：症状がないまま進む → 気づきにくい → 検査でわかる */}
        <div className="my-8 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 text-center">
            <span className="inline-flex w-9 h-9 rounded-full bg-slate-400 text-white font-bold items-center justify-center mb-2">1</span>
            <p className="font-bold text-slate-800">静かに進む</p>
            <p className="text-sm text-slate-500 mt-1">初期には、痛みなどの自覚症状がほとんどありません</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 text-center">
            <span className="inline-flex w-9 h-9 rounded-full bg-amber-400 text-white font-bold items-center justify-center mb-2">2</span>
            <p className="font-bold text-slate-800">症状だけでは気づきにくい</p>
            <p className="text-sm text-slate-500 mt-1">骨が弱くなっていても、日常生活では変化を感じないことがあります</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5 text-center">
            <span className="inline-flex w-9 h-9 rounded-full bg-emerald-500 text-white font-bold items-center justify-center mb-2">3</span>
            <p className="font-bold text-slate-800">検査で骨の状態が分かる</p>
            <p className="text-sm text-slate-500 mt-1">骨密度の測定は、骨折の危険性を判断する手がかりになります</p>
          </div>
        </div>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">だから「調べておく」ことに意味があります</h2>
        <p>
          骨粗しょう症は、<span className="font-bold text-slate-800">症状だけでは分かりにくい</span>病気です。そのため、骨折する前に一度、骨の状態を調べておくことが大切です。骨粗しょう症を早めに発見し、必要に応じて生活習慣の改善や適切な治療を行うことで、<span className="font-bold text-emerald-700">骨折の危険性を減らす効果が期待できます</span>。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">当院の骨密度検査（DXA法）</h2>
        <p>
          当院では、<span className="font-bold text-slate-800">DXA（デキサ）法</span>による骨密度検査を行っています。DXA法は、2種類の微量なX線を使用して骨密度を測定する方法です。当院では、背骨の腰椎と、足のつけ根にあたる大腿骨近位部を測定します。
        </p>
        <p>
          腰椎と大腿骨近位部は、骨粗しょう症による骨折が起こりやすく、診断や治療方針を考えるうえで重要な部位です。
        </p>
        <p>
          検査中は検査台に<span className="font-bold text-slate-800">仰向けに寝ていただきます</span>。痛みを伴う検査ではなく、撮影は短時間で終了します。X線を使用しますが、被ばく量はごく少量です。
        </p>
        <div className="bg-sky-50 border border-sky-100 rounded-xl p-5 text-sm text-slate-600">
          骨密度を調べる方法には、かかとの骨を超音波で測定する検査などもあります。超音波検査は手軽に受けられる一方、骨粗しょう症の診断や治療効果の確認には、一般に腰椎や大腿骨近位部を測定するDXA法が用いられます。
        </div>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">こんな方は、一度調べてみませんか</h2>
        <p>次のような方は、骨密度検査について医師に相談することをおすすめします。</p>
        <ul className="space-y-2">
          {[
            '閉経後の女性',
            'ご高齢の方',
            '身長が以前より縮んだ方',
            '背中や腰が丸くなってきた方',
            '軽い転倒などで骨折したことがある方',
            'ご両親に大腿骨近位部骨折の既往がある方',
            'ステロイド薬を長期間使用している方',
            '喫煙習慣がある方',
            '飲酒量が多い方',
            '体重が軽い方',
            '関節リウマチなど、骨粗しょう症と関係する病気がある方',
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={20} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm text-slate-500">
          ※ あてはまる項目があっても、必ず骨粗しょう症というわけではありません。検査や治療が必要かどうかは、年齢、骨折歴、持病、服用している薬などを踏まえて、医師が総合的に判断します。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">骨折する前に、骨の状態を確認しましょう</h2>
        <p>
          骨密度検査をご希望の方は、<span className="font-bold text-slate-800">受診の際にお気軽にご相談ください</span>（ご予約は不要です）。「痛くないから大丈夫」ではなく、「症状がない今のうちに、一度調べておく」。骨の状態を知ることは、これからも自分の足で歩き続けるための、大切な備えになります。
        </p>

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 flex items-start gap-3">
          <BookOpen className="text-emerald-600 shrink-0 mt-0.5" size={22} />
          <p className="text-emerald-900">
            この記事は、骨折する<span className="font-bold">「前」</span>のお話です。一度骨折した方には、次の骨折を防ぐための継続的な取り組みが重要です。詳しくは、
            <span className="font-bold">「一度の骨折を、二度と繰り返さないために」</span>の記事もあわせてご覧ください。
          </p>
        </div>

        <div className="mt-8 pt-4 border-t border-slate-100 text-sm text-slate-500">
          <p className="font-bold mb-1">参考資料</p>
          <ul className="space-y-1">
            <li>骨粗鬆症の予防と治療ガイドライン2025年版</li>
            <li>公益財団法人 骨粗鬆症財団</li>
            <li>公益社団法人 日本整形外科学会「骨粗鬆症（骨粗しょう症）」</li>
            <li>日本生活習慣病予防協会「骨粗鬆症」統計データ</li>
          </ul>
        </div>
      </div>
    )
  },  {
    id: 4,
    date: '2026.07.19',
    category: '当院の取り組み',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    title: '新しいリハビリ機器を2台、導入しました ― 痛み・こわばりへの新たなお手伝い',
    excerpt: 'リハビリをより充実させるため、新しい治療機器を2台導入しました。「電気で」と「温めて」、2つの力で、痛みやこわばりでお困りの方のお手伝いをします。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          このたび原田整形外科では、リハビリテーションをより充実させるため、<span className="font-bold">新しい治療機器を2台導入しました</span>。どちらも、腰・肩・膝・足首などの<span className="font-bold">痛みやこわばり</span>でお困りの方への、リハビリの心強い味方です。かんたんにご紹介します。
        </p>

        {/* 図：2台のカード */}
        <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-sky-700 bg-sky-100 rounded-full px-3 py-0.5 mb-2">電気で</span>
            <p className="font-bold text-slate-800">カイネタイザー KT-108＋</p>
            <p className="text-sm text-slate-500 mt-1">電気刺激で、リハビリを補助</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
            <span className="inline-block text-xs font-bold text-amber-700 bg-amber-100 rounded-full px-3 py-0.5 mb-2">温めて</span>
            <p className="font-bold text-slate-800">マイクロタイザー MT-5DLL</p>
            <p className="text-sm text-slate-500 mt-1">マイクロ波で、体組織を温める</p>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">カイネタイザー KT-108＋（電気刺激によるリハビリ）</h2>
          <p>低周波や干渉低周波などの電気刺激を、皮膚表面から筋肉や神経に加える機器です。痛みをやわらげたり、筋肉の働きを保つためのリハビリを補助したりする目的で、患者さんの状態に応じて使用します。</p>
          <figure className="my-4">
            <img src="/images/rehab-kinetizer.jpg" alt="カイネタイザー KT-108＋" loading="lazy" className="rounded-xl border border-slate-200 w-full max-w-md mx-auto" />
            <figcaption className="text-center text-sm text-slate-500 mt-2">カイネタイザー KT-108＋</figcaption>
          </figure>
        </div>

        <div>
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">マイクロタイザー MT-5DLL（マイクロ波による温熱療法）</h2>
          <p>マイクロ波を患部に照射し、体組織を加温する温熱療法機器です。温熱療法の一つとして、痛みやこわばりのある部位などに、医師やスタッフが状態を確認しながら使用します。リハビリ前の準備として使用することもあります。</p>
          <figure className="my-4">
            <img src="/images/rehab-microtizer.jpg" alt="マイクロタイザー MT-5DLL" loading="lazy" className="rounded-xl border border-slate-200 w-full max-w-md mx-auto" />
            <figcaption className="text-center text-sm text-slate-500 mt-2">マイクロタイザー MT-5DLL</figcaption>
          </figure>
        </div>

        <div>
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-2">「電気で」と「温めて」、2つの力で</h2>
          <p>性格の違う2台がそろったことで、患者さんお一人おひとりの状態に合わせて、より幅広くお手伝いできるようになりました。</p>
        </div>

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6">
          <p className="font-bold text-slate-800 mb-2">安心してお使いいただくために</p>
          <p className="text-slate-700">
            使用できるかどうかは、機器や治療部位、患者さんの状態によって異なります。特に、ペースメーカーなどの植込み型医用電子機器、人工関節などの体内金属がある方、妊娠中または妊娠の可能性がある方は、必ず事前にスタッフへお申し出ください。これらの機器は、医師やスタッフが患者さんの状態を確認したうえで使用します。効果の感じ方には個人差があります。
          </p>
        </div>
      </div>
    )
  },

  {
    id: 3,
    date: '2026.07.09',
    category: '健康のはなし',
    categoryColor: 'bg-sky-100 text-sky-700',
    title: 'お子さん、片足で5秒立てますか？ ― 「子どもロコモ」のお話',
    excerpt: '片足で5秒立てますか？かかとをつけてしゃがめますか？ 基本の動きがうまくできない「子どもロコモ」が増えています。家庭でできるチェックと、遊びでできる対策のお話です。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          「お子さんは、片足でふらつかず5秒間立てますか？」「かかとを床につけたまま、しゃがめますか？」——最近、こうした基本の動きがうまくできない子どもが増えているといわれています。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-sky-400 pl-3">「子どもロコモ」とは？</h2>
        <p>
          「ロコモ（ロコモティブシンドローム）」は、もともとご高齢の方の、足腰など“運動器”の衰えを指す言葉です。それが今、子どもたちにも心配されています。姿勢のくずれや運動不足などで、体をうまく動かせない状態を<span className="font-bold text-sky-700">「子どもロコモ」</span>と呼びます（正式な病名ではなく、みんなに気づいてもらうための呼び名です）。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-sky-400 pl-3">おうちでできる、4つのチェック</h2>
        <p>全国ストップ・ザ・ロコモ協議会が、家庭でできる簡単なチェックを紹介しています。月に1回、遊び感覚で試してみてください。</p>

        {/* 図：4つのチェック（2×2） */}
        <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['片足立ち', '片足で、ふらつかず5秒以上立てる'],
            ['しゃがみ込み', 'かかとを浮かさず、後ろに倒れずにしゃがめる'],
            ['バンザイ', '両腕を、耳の後ろまでまっすぐ上げられる'],
            ['前屈', '立ったまま膝を伸ばして、指が楽に床につく'],
          ].map(([t, d], i) => (
            <div key={i} className="bg-sky-50 border border-sky-100 rounded-xl p-4 flex items-start gap-3">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-500 text-white font-bold flex items-center justify-center">{i + 1}</span>
              <div>
                <p className="font-bold text-slate-800">{t}</p>
                <p className="text-sm text-slate-500 mt-0.5">{d}</p>
              </div>
            </div>
          ))}
        </div>

        <p>
          1つでもできないものがあれば、子どもロコモが疑われます。ある調査では、<span className="font-bold">約4割の子ども</span>が1つ以上できなかったとの報告もあります。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-sky-400 pl-3">なぜ増えているの？</h2>
        <p>
          背景には、子どもの運動時間の減少や“二極化”（よく動く子と、ほとんど動かない子の差）があるといわれます。スポーツ庁の調査でも、コロナ禍のあと、運動時間の減少や画面を見る時間の増加が確認されています。転ぶときにとっさに手が出ず、大きなケガにつながる例もあります。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-sky-400 pl-3">特別な運動はいりません。遊びが一番の薬です</h2>
        <p>毎日の外遊び・散歩・ボール遊びが、いちばんの近道です。楽しく続く工夫を少しだけ。</p>
        <ul className="bg-slate-50 rounded-lg border border-slate-100 p-5 space-y-2">
          <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-sky-500 shrink-0 mt-0.5" /><span><span className="font-bold">「だるまさんがころんだ」</span>で、鬼が振り向いたら“片足立ち”や“しゃがんだ姿勢”で止まるルールに</span></li>
          <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-sky-500 shrink-0 mt-0.5" /><span><span className="font-bold">タオルの引っ張り合い</span>を、片足立ちで</span></li>
        </ul>
        <p className="text-slate-700">
          教室に週1回通うよりも、毎日少しずつ続けるほうが力になります。保護者の方も一緒に動くと、大人のロコモ予防にもなって一石二鳥です。
        </p>

        <div className="bg-sky-50 border border-sky-100 rounded-xl p-6">
          <p className="text-slate-700">
            <span className="font-bold text-slate-800">「子どもロコモ」の多くは、病気ではなく“運動不足のサイン”です。</span>
            まずはご家庭で、毎日の遊びや運動を通じて体を動かすことが、いちばんの対策になります。そのうえで、<span className="font-bold">痛みがある・体の動きに気になる左右差がある・ケガをした</span>——といった場合は、整形外科で一度みてもらうと安心です。気になる症状があるときは、どうぞご相談ください。
          </p>
        </div>

        <div className="mt-8 pt-4 border-t border-slate-100 text-sm text-slate-500">
          <p className="font-bold mb-1">参考</p>
          <ul className="space-y-1">
            <li>
              <a href="https://sloc.or.jp/?page_id=165" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                認定NPO法人 全国ストップ・ザ・ロコモ協議会「子どもロコモについて」
              </a>
            </li>
            <li>
              <a href="https://www.joa.or.jp/public/locomo/index.html" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                日本整形外科学会「ロコモティブシンドローム」
              </a>
            </li>
          </ul>
        </div>
      </div>
    )
  },

  {
    id: 2,
    date: '2026.07.07',
    category: '健康のはなし',
    categoryColor: 'bg-sky-100 text-sky-700',
    title: '「フレイル」「サルコペニア」って知っていますか？ ― 年齢による衰えは、防げる・遅らせられる',
    excerpt: '「最近疲れやすい」「歩くのが遅くなった」…年のせいとあきらめる前に。フレイル・サルコペニアを知って、気づいて、防ぐためのお話です。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          「最近、疲れやすくなった」「歩くのが遅くなった」「ペットボトルのふたが開けにくい」――こうした変化を、「年のせいだから仕方ない」とあきらめていませんか？ 実はその一歩手前で、<span className="font-bold text-slate-800">気づいて手を打てば、元気を保てる可能性がある</span>のです。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3">「フレイル」とは？</h2>
        <p>
          「フレイル」とは、健康な状態と、介護が必要な状態の<span className="font-bold text-emerald-700">ちょうど間</span>のこと。心と体の元気が少しだけ落ちてきた状態を指します。大切なのは、フレイルは<span className="font-bold text-emerald-700">早めに気づいて対策すれば、元気な状態に戻せることがある</span>という点です。

        </p>

        {/* 図1：健康 → フレイル → 要介護 の帯 */}
        <div className="my-8">
          <div className="flex items-stretch gap-1 text-center text-sm">
            <div className="flex-1 bg-emerald-100 text-emerald-800 rounded-l-lg py-4 px-2 font-bold flex items-center justify-center">健康</div>
            <div className="flex-1 bg-amber-100 text-amber-800 py-4 px-2 font-bold ring-2 ring-amber-300 rounded flex flex-col items-center justify-center">
              フレイル
              <span className="block text-xs font-normal mt-0.5">健康と要介護の“あいだ”</span>
            </div>
            <div className="flex-1 bg-slate-200 text-slate-600 rounded-r-lg py-4 px-2 font-bold flex items-center justify-center">要介護</div>
          </div>
          <p className="text-center text-xs text-emerald-700 mt-2 font-bold">※ 早めに気づけば、健康な状態に戻せることも</p>
        </div>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3">「サルコペニア」とは？</h2>
        <p>
          「サルコペニア」とは、年齢とともに<span className="font-bold text-emerald-700">筋肉が減り、力も弱くなっていくこと</span>。フレイルの大きな原因のひとつです。筋肉が減ると、転びやすくなり、骨折にもつながりやすくなります。
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3">おうちでできる、かんたんチェック</h2>
        <p>次のことに心当たりはありませんか？</p>
        <ul className="bg-slate-50 rounded-lg border border-slate-100 p-5 space-y-2">
          <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> ペットボトルのふたが開けにくい</li>
          <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> 以前より歩くのが遅くなった</li>
          <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> つまずいたり、転びやすくなった</li>
          <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> 階段を上るのが億劫になった</li>
          <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> この半年で、体重が2〜3kg以上減った</li>
        </ul>

        <div className="bg-sky-50 border border-sky-100 rounded-xl p-6">
          <p className="font-bold text-slate-800 mb-2">＋ ゆびわっかテスト</p>
          <p className="text-slate-700 text-sm">
            両手の親指と人さし指で輪をつくり、ふくらはぎの一番太いところを軽く囲んでみてください。<span className="font-bold">囲めずにはみ出す</span>なら筋肉はしっかり。<span className="font-bold text-sky-700">ちょうど囲める、すき間ができる</span>という方は、筋肉が減っているサインかもしれません。
          </p>
        </div>

        <p className="text-slate-700">
          いくつか当てはまっても、心配しすぎないでください。<span className="font-bold text-emerald-700">大切なのは「気づくこと」。気づけば、対策できます。</span>
        </p>

        <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3">防ぐ・遅らせる、3つの鍵</h2>

        {/* 図2：動く・食べる・つながる */}
        <div className="my-6 grid grid-cols-3 gap-3 text-center">
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <Footprints className="mx-auto text-emerald-600 mb-2" size={28} />
            <p className="font-bold text-slate-800 text-sm">動く</p>
            <p className="text-xs text-slate-500 mt-1">無理のない運動で<br />筋肉に刺激を</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <Utensils className="mx-auto text-amber-600 mb-2" size={28} />
            <p className="font-bold text-slate-800 text-sm">食べる</p>
            <p className="text-xs text-slate-500 mt-1">肉・魚・卵・大豆など<br />たんぱく質をしっかり</p>
          </div>
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-4">
            <Users className="mx-auto text-sky-600 mb-2" size={28} />
            <p className="font-bold text-slate-800 text-sm">つながる</p>
            <p className="text-xs text-slate-500 mt-1">人と会い、外に出る。<br />社会とのつながりも</p>
          </div>
        </div>
        <p className="text-sm text-slate-500">※ 3つは、組み合わせて続けるほど効果が高まるといわれています。</p>

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6">
          <p className="text-emerald-900">
            原田整形外科では、リハビリや骨粗しょう症のケアを通して、こうした「衰えの予防」のお手伝いをしています。気になることがあれば、どうぞお気軽にご相談ください。
          </p>
        </div>

        <div className="mt-8 pt-4 border-t border-slate-100 text-sm text-slate-500">
          <p className="font-bold mb-1">参考</p>
          <ul className="space-y-1">
            <li>
              <a href="https://www.tyojyu.or.jp/net/byouki/frailty/about.html" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                健康長寿ネット「フレイルとは」（長寿科学振興財団）
              </a>
            </li>
            <li>
              <a href="https://www.mhlw.go.jp/stf/houdou_kouhou/kouhou_shuppan/magazine/202111_00001.html" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                厚生労働省 広報誌「フレイル予防」
              </a>
            </li>
          </ul>
        </div>
      </div>
    )
  },

  {
    id: 1,
    date: '2026.07.07',
    category: '当院の取り組み',
    categoryColor: 'bg-emerald-100 text-emerald-700',
    title: '一度の骨折を、二度と繰り返さないために ― 当院の再骨折予防の取り組み',
    excerpt: 'ご高齢の方に多い「太ももの付け根」の骨折。一度骨折すると再び骨折するリスクが高まりますが、きちんと備えれば防げる可能性があります。当院の再骨折予防の取り組みをご紹介します。',
    content: (
      <div className="space-y-5 leading-relaxed">
        <p>
          年齢を重ねると、ちょっとした転倒で骨を折ってしまうことがあります。中でも「太ももの付け根（大腿骨の付け根）」の骨折は、ご高齢の方に多く見られるものです。
        </p>
        <p>
          あまり知られていませんが、<span className="font-bold text-slate-800">一度骨折した方は、その後にもう一度骨折してしまうリスクが高くなる</span>ことがわかっています。せっかく治って歩けるようになっても、再び骨折して寝たきりに近づいてしまう――これはご本人にとっても、ご家族にとっても、とてもつらいことです。
        </p>
        <p>
          そして大切なのは、<span className="font-bold text-emerald-700">この「二度目の骨折」は、きちんと備えれば防げる可能性がある</span>ということです。骨がもろくなる「骨粗しょう症」の検査と治療を、骨折をきっかけにしっかり始めて、続けていくことが鍵になります。ところが実際には、骨折後に骨粗しょう症の治療まで続けられている方は多くない、という報告もあります。
        </p>

        {/* 流れの図：一度目の骨折 → 検査と治療 → 二度目を防ぐ */}
        <div className="my-8 grid gap-2">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 flex items-center gap-4">
            <span className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-400 text-white font-bold flex items-center justify-center">1</span>
            <div>
              <p className="font-bold text-slate-800">一度目の骨折</p>
              <p className="text-sm text-slate-500">太ももの付け根など</p>
            </div>
          </div>
          <div className="flex justify-center text-slate-300"><ChevronDown size={24} /></div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5 flex items-center gap-4">
            <span className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center">2</span>
            <div>
              <p className="font-bold text-slate-800">検査と治療</p>
              <p className="text-sm text-slate-500">骨の状態を調べ、骨粗しょう症の治療を開始（医師・看護師・薬剤師のチームで）</p>
            </div>
          </div>
          <div className="flex justify-center text-slate-300"><ChevronDown size={24} /></div>
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-5 flex items-center gap-4">
            <span className="flex-shrink-0 w-9 h-9 rounded-full bg-sky-500 text-white font-bold flex items-center justify-center">3</span>
            <div>
              <p className="font-bold text-slate-800">二度目を防ぐ</p>
              <p className="text-sm text-slate-500">退院後も続けて見守り、再骨折を予防</p>
            </div>
          </div>
        </div>

        <p>
          こうした状況を受けて、国は2022年（令和4年）に、<span className="font-bold text-slate-800">「骨折した方の“次の骨折”を防ぐ取り組み」を医療の仕組みとして後押しする制度</span>を新しく設けました。医師・看護師・薬剤師などがチームになって、骨折の治療だけでなく、その後の骨粗しょう症の評価・治療・見守りまで一緒に行っていく、という考え方です。
        </p>
        <p>
          さらに2024年（令和6年）には、この取り組みを、当院のような<span className="font-bold text-slate-800">入院ベッドを持つ地域のクリニック（有床診療所）</span>でも担えるように制度が広がりました。
        </p>

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 flex items-start gap-3">
          <ShieldPlus className="text-emerald-600 shrink-0 mt-0.5" size={22} />
          <p className="text-emerald-900">
            <span className="font-bold">原田整形外科でも、この考え方に沿って再骨折予防に取り組んでいます。</span>
            骨折で入院された患者さんには、骨の状態を調べ、必要な治療を始め、退院後も続けて見守っていく――そんなお手伝いをスタッフ一同で行っています。
          </p>
        </div>

        <p>
          「一度の骨折を、二度と繰り返さないために」。気になることがあれば、どうぞお気軽にスタッフにお声かけください。
        </p>
      </div>
    )
  },
];

// --- サブコンポーネント（部品）の定義 ---

// 特徴カード
const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300">
    <div className="text-emerald-500 mb-4 bg-emerald-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto md:mx-0">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-slate-800 mb-3 text-center md:text-left">{title}</h3>
    <p className="text-slate-600 leading-relaxed text-center md:text-left">
      {desc}
    </p>
  </div>
);

// 診療案内アイテム
const ServiceItem = ({ title, sub }) => (
  <div className="flex items-center p-4 border border-slate-200 rounded-lg hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors">
    <div className="w-2 h-2 bg-emerald-400 rounded-full mr-4"></div>
    <div>
      <h4 className="font-bold text-slate-800">{title}</h4>
      <p className="text-xs text-slate-500">{sub}</p>
    </div>
  </div>
);

// 共通ヘッダー
const Header = ({ onNavigate, isMenuOpen, setIsMenuOpen, toggleMenu }) => {
  const navLinks = [
    { name: 'ホーム', id: 'home' },
    { name: '診療案内', id: 'home', hash: '#services' },
    { name: '入院案内', id: 'home', hash: '#inpatient' },
    { name: '院長紹介', id: 'home', hash: '#doctor' },
    { name: 'アクセス', id: 'home', hash: '#access' },
    { name: 'クリニックだより', id: 'blog' },
    { name: '採用情報', id: 'home', hash: '#recruit' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => onNavigate('home')}
        >
          <div className="bg-emerald-500 text-white p-2 rounded-lg">
            <HeartHandshake size={24} />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-800 tracking-tight">原田整形外科医院</h1>
            <p className="text-xs text-slate-500 hidden md:block">地域の皆様の健やかな生活をサポート</p>
          </div>
        </div>

        <nav className="hidden xl:flex items-center gap-5">
          {navLinks.map((link, idx) => (
            <button
              key={idx}
              onClick={() => {
                onNavigate(link.id);
                if (link.hash) {
                  setTimeout(() => {
                    const el = document.querySelector(link.hash);
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="text-sm font-medium hover:text-emerald-600 transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer"
            >
              {link.name}
            </button>
          ))}
          <button
            onClick={() => {
              onNavigate('home');
              setTimeout(() => {
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-700 transition-colors"
          >
            お問い合わせ
          </button>
        </nav>

        <button
          className="xl:hidden p-2 text-slate-600"
          onClick={toggleMenu}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="xl:hidden bg-white border-t border-slate-100 absolute w-full shadow-xl">
          <div className="flex flex-col p-4 gap-4">
            {navLinks.map((link, idx) => (
              <button
                key={idx}
                className="text-left text-lg font-medium py-2 border-b border-slate-50 text-slate-600"
                onClick={() => {
                  setIsMenuOpen(false);
                  onNavigate(link.id);
                  if (link.hash) {
                    setTimeout(() => {
                      const el = document.querySelector(link.hash);
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }
                }}
              >
                {link.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

// 共通フッター
const Footer = ({ onNavigate }) => (
  <footer className="bg-slate-900 text-slate-400 py-10 text-sm">
    <div className="container mx-auto px-4">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div className="col-span-1 md:col-span-2">
          <h3 className="text-white text-lg font-bold mb-4">原田整形外科医院</h3>
          <p className="mb-4">地域のみなさまに信頼される<br />クリニックを目指しています。</p>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">メニュー</h4>
          <ul className="space-y-2">
            <li><button onClick={() => onNavigate('home')} className="hover:text-emerald-500">ホーム</button></li>
            <li><button onClick={() => onNavigate('blog')} className="hover:text-emerald-500">クリニックだより</button></li>
            <li><button onClick={() => onNavigate('rehab')} className="hover:text-emerald-500">訪問リハビリ</button></li>
            <li><button onClick={() => onNavigate('care')} className="hover:text-emerald-500">オルソ居宅介護支援センター</button></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">診療科目</h4>
          <ul className="space-y-2">
            <li>整形外科</li>
            <li>リハビリテーション科</li>
            {/* スポーツ整形を削除 */}
            <li>骨粗鬆症外来</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 pt-8 text-center space-y-3">
        <button
          onClick={() => onNavigate('referral')}
          className="text-slate-500 hover:text-emerald-500 text-xs underline underline-offset-2"
        >
          医療・介護関係者の方へ（入院・手術のご紹介）
        </button>
        <p>&copy; 2025 Harada Orthopedics Clinic. All Rights Reserved.</p>
      </div>
    </div>
  </footer>
);

// --- ページコンポーネント ---

// 1. ホームページ（メイン）
const HomePageContent = ({ navigateTo, openNews, openBlog, formStatus, handleSubmit }) => (
  <div className="animate-in fade-in duration-500">
    {/* Hero Section */}
    <section id="home" className="relative bg-emerald-50 py-20 md:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-white/50 -z-10"></div>
      <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-10">
        <div className="md:w-1/2 z-10">
          <span className="inline-block bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold mb-6">
            平日18時まで受付
          </span>
          <div className="text-slate-700 space-y-5 leading-relaxed text-justify md:text-left">
            <p className="font-bold text-lg">
              原田整形外科医院のホームページをご覧いただきありがとうございます。<br />
              当院は、<span className="text-emerald-700">総社市で開院して25年</span>。地域の皆様に親しまれる地域密着型の有床診療所として歩んでまいりました。
            </p>
            <p>
              「痛み」は日常生活の質を大きく低下させます。私たちは、正確な診断と適切な治療はもちろんのこと、理学療法士によるリハビリテーションにも力を入れ、痛みの根本的な解決を目指します。
            </p>
            <p>
              また、19床の入院設備を備えており、手術後のリハビリや、ご自宅での生活が不安な方の短期入院など、患者様やご家族のニーズに合わせた柔軟な対応が可能です。スタッフ一同、温かい心で皆様をお迎えいたします。
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <a href="#access" className="flex items-center justify-center gap-2 bg-slate-800 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-700 transition-colors">
              <Clock size={20} />
              受付時間を見る
            </a>
            <a href="tel:0866948881" className="flex items-center justify-center gap-2 bg-white text-emerald-600 border-2 border-emerald-600 px-6 py-3 rounded-lg font-bold hover:bg-emerald-50 transition-colors">
              <Phone size={20} />
              0866-94-8881
            </a>
          </div>
        </div>
        <div className="md:w-1/2 relative w-full">
          {/* 画像表示エリア：写真を使いつつ、明るさと彩度を上げて「青空」を強調する補正クラスを追加 */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-200 aspect-video group border-4 border-white">
            {/* 【重要】実際の運用時は、HEIC画像をJPGなどに変換し、正しいパスを指定してください。
                IMG_1728.JPG を配置したら、srcをそのパスに変更してください。
             */}
            <img
              src="/26922788-A5B5-4573-95D5-286891A338CE_1_105_c.jpeg"
              alt="原田整形外科医院 外観"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 brightness-110 saturate-110 contrast-105"
            />




            {/* ワンポイント：写真が少し古くても、周りに白い枠（border-4 border-white）をつけたり、
                少し明るく補正（brightness-110）することで、
                「古ぼけた」ではなく「レトロで味のある」雰囲気に変わります。
             */}
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg hidden md:block animate-bounce-slow">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 p-2 rounded-full text-yellow-600">
                <Calendar size={20} />
              </div>
              <div>

                <p className="text-sm font-bold">リハビリ予約受付中</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Information & Related Facilities Section */}
    <section className="bg-white py-12 border-b border-slate-100">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-12 gap-10">
          {/* Left Column: Related Facilities */}
          <div className="md:col-span-4 lg:col-span-3">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg overflow-hidden shadow-sm">
              <div className="bg-emerald-500 text-white p-4 font-bold text-center text-lg">
                関連事業・連携施設
              </div>
              <div className="divide-y divide-emerald-100">
                {/* 訪問リハビリボタン (onClickでページ切り替え) */}
                <button
                  onClick={() => navigateTo('rehab')}
                  className="w-full text-left block p-5 hover:bg-white transition-colors group relative overflow-hidden"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2 text-emerald-800 font-bold">
                        <Car size={20} className="text-emerald-600" />
                        <span>訪問リハビリ</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        ご自宅での生活を支援するため、理学療法士が訪問してリハビリを行います。
                      </p>
                    </div>
                    <div className="bg-emerald-200 text-emerald-700 rounded-full p-1 mt-1 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                      <ChevronRight size={16} />
                    </div>
                  </div>
                </button>

                {/* 居宅介護支援ボタン (onClickでページ切り替え) */}
                <button
                  onClick={() => navigateTo('care')}
                  className="w-full text-left block p-5 hover:bg-white transition-colors group relative overflow-hidden"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2 text-emerald-800 font-bold">
                        <ClipboardList size={20} className="text-emerald-600" />
                        <span>オルソ居宅介護支援センター</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        介護保険の申請代行やケアプランの作成など、介護の相談窓口です。
                      </p>
                    </div>
                    <div className="bg-emerald-200 text-emerald-700 rounded-full p-1 mt-1 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                      <ChevronRight size={16} />
                    </div>
                  </div>
                </button>

                {/* サ高住情報（HPがないため、リンクではなく情報カードとして表示） */}
                <div className="w-full text-left block p-5 bg-slate-50/50">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 text-emerald-600 shrink-0">
                      <Building size={20} />
                    </div>
                    <div>
                      <div className="text-emerald-800 font-bold mb-2">
                        サービス付き高齢者向け住宅メイプル
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed mb-3">
                        当院と連携している高齢者向け住宅です（株式会社リコレ運営）。
                        退院後の住まいや、生活支援が必要な方のご相談先として連携しています。
                      </p>
                      {/* 問い合わせ先案内 */}
                      <div className="text-[10px] text-slate-500 bg-white p-2 rounded border border-slate-200 inline-block">
                        <span className="font-bold text-emerald-600">★ 詳しい情報は当院相談窓口まで</span><br />
                        パンフレット等のご用意もございますので、お気軽にお声がけください。
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: News */}
          <div className="md:col-span-8 lg:col-span-9">
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2 border-b border-slate-200 pb-3">
              <span className="w-1 h-6 bg-emerald-500 rounded-full"></span>
              お知らせ
            </h3>
            <ul className="space-y-4">
              {NEWS_DATA.map((item) => (
                <li key={item.id} className="bg-slate-50 rounded-lg hover:bg-emerald-50/50 transition-colors p-3 md:p-4">
                  {/* クリック時に openNews 関数を呼び出す */}
                  <button
                    onClick={() => openNews(item)}
                    className="w-full text-left flex flex-col md:flex-row md:items-center gap-3 group"
                  >
                    <div className="flex items-center gap-3 min-w-fit">
                      <span className="text-slate-500 font-mono text-sm">{item.date}</span>
                      <span className={`${item.categoryColor} text-xs px-2 py-0.5 rounded font-medium w-24 text-center`}>{item.category}</span>
                    </div>
                    <span className="text-slate-700 font-medium group-hover:text-emerald-600 transition-colors flex-1">
                      {item.title}
                    </span>
                    <ChevronRight size={16} className="hidden md:block text-slate-300 group-hover:text-emerald-500 ml-auto" />
                  </button>
                </li>
              ))}
            </ul>
            {/* お知らせ一覧ボタンは現在不要のため削除しています（将来記事が増えたら復活可能です） */}
          </div>
        </div>
      </div>
    </section>

    {/* Features */}
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">当院の特徴</h2>
          <p className="text-slate-600">患者様に安心して通っていただけるクリニックを目指しています</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<ShieldPlus size={32} />} // 変更: ShieldPlus（安心・医療）
            title="専門医による診療"
            desc="日本整形外科学会専門医および股関節外科専門医として、豊富な臨床経験に基づいた的確な診断を行います。"
          />
          <FeatureCard
            icon={<Activity size={32} />} // 現状維持: Activity（活動的）
            title="充実したリハビリテーション"
            desc="理学療法士による運動療法と、物理療法機器を組み合わせた治療を行います。"
          />
          <FeatureCard
            icon={<Users size={32} />} // 変更: Users（全世代、みんな）
            title="お子様からご高齢の方まで"
            desc="スポーツ外傷から骨粗鬆症のケアまで、幅広い世代の運動器疾患に対応します。"
          />
        </div>
      </div>
    </section>

    {/* 追加：フレイル・サルコペニア予防セクション */}
    <section className="py-12 bg-emerald-50/50 border-y border-emerald-100">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-10 flex flex-col md:flex-row gap-8 items-center border border-emerald-100">
          <div className="md:w-1/2">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold mb-4">
              <Footprints size={14} /> 健康寿命を延ばそう
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              「最近、体力が落ちた」と<br />
              感じていませんか？
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              加齢とともに筋力や心身の活力が低下する状態を<span className="font-bold text-emerald-700">「フレイル（虚弱）」</span>、
              筋肉量が減少することを<span className="font-bold text-emerald-700">「サルコペニア」</span>と呼びます。
              <br /><br />
              当院では、これらを予防・改善し、いつまでも自分の足で歩き続けられるよう、お一人おひとりに合わせたリハビリテーションも行っています。
            </p>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
              <span className="font-bold text-slate-700 block mb-2 text-sm">こんな症状はありませんか？</span>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle2 size={16} className="text-yellow-500" /> ペットボトルの蓋が開けにくい
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle2 size={16} className="text-yellow-500" /> 以前より歩くのが遅くなった
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle2 size={16} className="text-yellow-500" /> つまずいたり転びやすくなった
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle2 size={16} className="text-yellow-500" /> 階段を上るのが億劫になった
                </li>
              </ul>
            </div>
            <p className="mt-4 text-sm font-bold text-emerald-600">
              ★ 当院にてリハビリ目的の受診も受け付けております。お気軽にご相談ください。
            </p>
            <button
              onClick={() => openBlog(BLOG_DATA.find((p) => p.id === 2))}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 hover:text-emerald-800 hover:gap-2.5 transition-all bg-transparent border-none cursor-pointer p-0"
            >
              <BookOpen size={16} /> 「フレイル・サルコペニア」について詳しく読む
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="md:w-1/2 w-full flex justify-center">
            {/* イメージイラスト（フレイル予防）デザイン修正：写真なしでも見栄え良く */}
            <div className="bg-white w-full max-w-sm aspect-video rounded-xl flex items-center justify-center text-slate-400 flex-col gap-2 border-2 border-emerald-100 shadow-sm relative overflow-hidden">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-yellow-100 rounded-full opacity-50"></div>
              <Footprints size={64} className="text-emerald-200 z-10" />
              <span className="text-xs font-bold text-emerald-700/50 z-10 mt-2">いつまでも、自分の足で。</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Services */}
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-12">
          <div className="md:w-1/3">
            <h2 className="text-3xl font-bold text-slate-800 mb-6 relative inline-block">
              診療案内
              <span className="absolute bottom-1 left-0 w-full h-3 bg-emerald-200/50 -z-10"></span>
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              急な怪我から慢性の痛みまで幅広く対応いたします。
              リハビリテーションの体制も充実しております。
            </p>
            {/* 軽い怪我についての案内を追加 */}
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-100 text-sm leading-relaxed text-slate-700">
              <p className="font-bold text-emerald-700 mb-2 flex items-center gap-1">
                <CheckCircle2 size={16} />
                お気軽にご相談ください
              </p>
              すり傷、切り傷、やけどなどの軽いお怪我や、「これくらいで病院に行ってもいいのかな？」と思われるような些細な痛みでも、どうぞ遠慮なくご受診ください。
            </div>
          </div>
          <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* 診療科目を3つに変更 */}
            <ServiceItem title="整形外科" sub="骨折・捻挫・腰痛・関節痛・スポーツ外傷" />
            <ServiceItem title="リハビリテーション科" sub="運動療法・物理療法" />
            <ServiceItem title="骨粗鬆症外来" sub="骨密度検査・投薬治療" />

            {/* 4つ目の枠が空くので、案内用のボックスを表示してバランスを取る */}
            <div className="flex items-center p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 text-slate-500 justify-center">
              <span className="text-sm">各種保険取扱</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Inpatient Information */}
    <section id="inpatient" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg flex flex-col md:flex-row">
          {/* 入院案内画像のプレースホルダー修正：清潔感のあるデザインに */}
          <div className="md:w-1/2 relative min-h-[300px] bg-emerald-50 flex items-center justify-center text-slate-400 flex-col gap-2 border-r border-slate-100">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-sm text-emerald-400 mb-2">
              <Bed size={40} />
            </div>
            <span className="font-bold text-sm text-emerald-800/60">Inpatient Care</span>
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-white/0 to-emerald-100/30 pointer-events-none"></div>
          </div>
          <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-emerald-600 font-bold mb-2">
              <Bed size={24} />
              <span>有床診療所（19床）</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-6">
              入院のご案内
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              当院は19床のベッドを有する有床診療所です。<br />
              手術を受けられる患者様や、リハビリテーションが必要な患者様のために、入院設備を整えています。
              <br /><br />
              家庭的な雰囲気の中で、安心して療養していただけるよう、医師・看護師・看護助手・理学療法士・スタッフ一同、親身になってサポートいたします。
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Doctor */}
    <section id="doctor" className="py-20 bg-emerald-50">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-emerald-600 font-bold mb-4 flex items-center gap-2">
              <span className="w-8 h-px bg-emerald-600"></span>
              院長ご挨拶
            </h3>
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 leading-tight">
                わかりやすく、<br />
                納得できる診療を
              </h2>
              <p className="font-bold text-xl text-slate-700">院長　原田 育生 <span className="text-sm font-normal text-slate-500">（北海道札幌市出身）</span></p>
            </div>

            <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed mb-8">
              <p className="mb-4">
                整形外科医として40年近く、現場でコツコツと治療を重ねてきました。
                患者さんが納得できる診療を心がけてまいります。些細なことでもお気軽にご相談ください。
              </p>
            </div>

            <div className="bg-emerald-50/50 p-6 rounded-lg mb-8 border border-emerald-100">
              <h4 className="font-bold text-emerald-700 mb-3 text-sm flex items-center gap-2">
                <HeartHandshake size={18} />
                当院のモットー
              </h4>
              <ol className="list-decimal list-inside text-slate-700 space-y-2 font-medium">
                <li>自分が受けたくない治療は、患者様には行いません。</li>
                <li>最新の医療知識に基づき、最善の治療を提供します。</li>
              </ol>
            </div>

            <div className="border-t border-slate-100 pt-8">
              <h4 className="font-bold text-slate-800 mb-4 text-sm text-slate-500 uppercase tracking-wider">経歴</h4>
              <div className="text-sm text-slate-600 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <div className="flex gap-2 border-b border-slate-50 pb-2">
                  <span className="w-24 shrink-0 font-mono text-slate-400">学歴</span>
                  <span>大阪大学 医学部 卒業</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-2">
                  <span className="w-24 shrink-0 font-mono text-slate-400">1984-1993</span>
                  <span>防衛医科大学 整形外科 助手・講師</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-2">
                  <span className="w-24 shrink-0 font-mono text-slate-400">1987-1988</span>
                  <span>ウェスタン オンタリオ大学 研究員</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-2">
                  <span className="w-24 shrink-0 font-mono text-slate-400">1993-1996</span>
                  <span>国立呉医療センター 整形外科 部長</span>
                </div>
                <div className="flex gap-2 border-b border-slate-50 pb-2">
                  <span className="w-24 shrink-0 font-mono text-slate-400">1996-1999</span>
                  <span>姫路赤十字病院 リハビリテーション科 部長</span>
                </div>
                <div className="flex gap-2 pb-2">
                  <span className="w-24 shrink-0 font-mono text-slate-400">1999-現在</span>
                  <span>
                    原田整形外科医院 開院<br />
                    <span className="text-xs text-slate-500">（累計手術件数：約2500件 ※2025年現在）</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Access & Hours */}
    <section id="access" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-slate-800 text-center mb-12">アクセス・受付時間</h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-emerald-600 text-white p-4 text-center font-bold text-lg">
                受付時間
              </div>
              <table className="w-full text-center">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 border-b border-slate-200">
                    <th className="py-3 font-medium">受付時間</th>
                    <th className="py-3 font-medium text-slate-400">月</th>
                    <th className="py-3 font-medium text-slate-400">火</th>
                    <th className="py-3 font-medium text-slate-400">水</th>
                    <th className="py-3 font-medium text-slate-400">木</th>
                    <th className="py-3 font-medium text-slate-400">金</th>
                    <th className="py-3 font-medium text-blue-400">土</th>
                    <th className="py-3 font-medium text-red-400">日</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-b border-slate-100">
                    <td className="py-4 text-sm font-bold bg-slate-50">9:00 - 11:30</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-slate-300">-</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-slate-300">-</td>
                  </tr>
                  <tr>
                    <td className="py-4 text-sm font-bold bg-slate-50">15:00 - 18:00</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-slate-300">-</td>
                    <td className="text-emerald-500 font-bold">●</td>
                    <td className="text-slate-300">-</td>
                    <td className="text-slate-300">-</td>
                  </tr>
                </tbody>
              </table>
              <div className="p-4 bg-slate-50 text-xs text-slate-500 leading-relaxed">
                ※ 上記は<span className="font-bold">受付時間</span>です。診療は午前9:30〜12:00、午後15:30〜18:30となります<br />
                ※ 休診日：木曜・土曜午後・日曜・祝日<br />
                ※ 受付は受付終了時間の15分前までにお願いします<br />
                <span className="font-bold text-emerald-600">※ 物理療法に関しては18:15まで受け付けています</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="bg-slate-200 w-full h-64 rounded-xl flex items-center justify-center relative overflow-hidden group">
              <iframe
                width="100%"
                height="100%"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="map"
                scrolling="no"
                src="https://maps.google.com/maps?width=100%&amp;height=600&amp;hl=en&amp;q=〒719-1125%20岡山県総社市井手1208-2&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                style={{ filter: 'grayscale(0.2) opacity(0.9)', border: 0 }}
              ></iframe>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-emerald-100 p-2 rounded-full text-emerald-600 mt-1">
                  <MapPin size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800">所在地</h4>
                  <p className="text-slate-600">
                    〒719-1125<br />
                    岡山県総社市井手1208-2
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-emerald-100 p-2 rounded-full text-emerald-600 mt-1">
                  <Stethoscope size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800">アクセス</h4>
                  <p className="text-slate-600">
                    東総社駅より徒歩20分<br />
                    駐車場あり
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Recruitment - 写真枠を削除してテキストのみに変更、介護士追加 */}
    <section id="recruit" className="py-20 bg-emerald-600 text-white">
      <div className="container mx-auto px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center"> {/* レイアウトを中央揃えに変更 */}
            <div className="w-full max-w-2xl text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-500/50 px-3 py-1 rounded-full text-emerald-50 text-xs font-bold mb-4">
                <Users size={16} /> 一緒に働きませんか？
              </div>
              <h2 className="text-3xl font-bold mb-6">採用情報</h2>
              <p className="text-emerald-50 text-lg mb-8 leading-relaxed">
                原田整形外科医院では、一緒に働くスタッフを募集しています。<br />
                地域の皆様の健康を支える、やりがいのある職場です。<br />
                見学も随時受け付けておりますので、お気軽にお問い合わせください。
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
                  <span className="font-bold block">募集職種</span>
                  <span className="text-emerald-100 text-sm">看護師・准看護師</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
                  <span className="font-bold block">募集職種</span>
                  <span className="text-emerald-100 text-sm">理学療法士</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
                  <span className="font-bold block">募集職種</span>
                  <span className="text-emerald-100 text-sm">介護士</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
                  <span className="font-bold block">募集職種</span>
                  <span className="text-emerald-100 text-sm">厨房スタッフ</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
                  <span className="font-bold block">募集職種</span>
                  <span className="text-emerald-100 text-sm">受付事務</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Contact Form */}
    <section id="contact" className="py-20 bg-slate-800 text-white">
      <div className="container mx-auto px-4 text-center max-w-2xl">
        <h2 className="text-3xl font-bold mb-6">お問い合わせ</h2>
        <p className="text-slate-300 mb-10">
          診療内容や地域医療連携等に関するご質問・ご相談は、<br />
          お電話・FAX・メールまたは下記フォームよりお問い合わせください。
        </p>

        <div className="bg-white text-slate-800 rounded-xl p-8 shadow-2xl max-w-3xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 mb-10 pb-8 border-b border-slate-100">
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-emerald-600 font-bold">
                <Phone size={20} />
                <span>電話</span>
              </div>
              <a href="tel:0866948881" className="text-lg font-bold text-slate-800 hover:text-emerald-600 hover:underline transition-colors">
                0866-94-8881
              </a>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-emerald-600 font-bold">
                <Printer size={20} />
                <span>FAX</span>
              </div>
              <p className="text-lg font-bold text-slate-800">0866-90-0015</p>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-emerald-600 font-bold">
                <Mail size={20} />
                <span>Email</span>
              </div>
              <p className="text-sm font-bold text-slate-800 break-all">haradaseikei8881@gmail.com</p>
            </div>
          </div>

          {formStatus === 'success' ? (
            <div className="py-10 text-center">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">送信完了しました</h3>
              <p className="text-slate-600">
                お問い合わせありがとうございます。<br />
                内容を確認次第、担当者よりご連絡させていただきます。
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-6 text-emerald-600 font-bold hover:underline"
              >
                戻る
              </button>
            </div>
          ) : (
            <form
              className="space-y-4"
              name="contact"
              method="POST"
              data-netlify="true"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="form-name" value="contact" />

              <div className="text-left">
                <label className="block text-sm font-bold text-slate-700 mb-1">お名前</label>
                <input type="text" name="name" className="w-full border border-slate-300 rounded p-3" placeholder="山田 太郎" required />
              </div>

              <div className="text-left">
                <label className="block text-sm font-bold text-slate-700 mb-1">メールアドレス</label>
                <input type="email" name="email" className="w-full border border-slate-300 rounded p-3" placeholder="example@email.com" required />
              </div>

              <div className="text-left">
                <label className="block text-sm font-bold text-slate-700 mb-1">お問い合わせ内容</label>
                <textarea name="message" className="w-full border border-slate-300 rounded p-3 h-32" placeholder="ご質問内容をご記入ください" required />
              </div>

              {formStatus === 'error' && (
                <div className="bg-red-50 text-red-600 p-3 rounded text-sm">
                  送信に失敗しました。時間をおいて再度お試しいただくか、お電話にてお問い合わせください。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-emerald-600 text-white font-bold py-4 rounded hover:bg-emerald-700 transition-colors disabled:opacity-70"
              >
                {formStatus === 'submitting' ? '送信中...' : '送信する'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>

    {/* 医療・介護関係者の方へ（控えめな案内） */}
    <section className="py-12 bg-slate-100 border-t border-slate-200">
      <div className="container mx-auto px-4 max-w-4xl">
        <button
          onClick={() => navigateTo('referral')}
          className="w-full text-left bg-white rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all p-6 flex flex-col sm:flex-row sm:items-center gap-4 group cursor-pointer"
        >
          <div className="bg-slate-800 text-white p-3 rounded-lg shrink-0">
            <Building size={24} />
          </div>
          <div className="flex-1">
            <p className="font-bold text-slate-800 group-hover:text-emerald-700 transition-colors">医療・介護関係者の方へ</p>
            <p className="text-sm text-slate-500 mt-0.5">入院・手術のご紹介について、受け入れ範囲や紹介の流れをご案内しています（ケアマネジャー様・医療機関の先生方へ）。</p>
          </div>
          <span className="inline-flex items-center text-emerald-600 font-bold text-sm shrink-0 group-hover:gap-1 transition-all">
            ご案内はこちら <ChevronRight size={18} />
          </span>
        </button>
      </div>
    </section>
  </div>
);

// 2. 訪問リハビリ詳細ページ
const VisitingRehabPage = ({ onBack }) => (
  <div className="animate-in fade-in duration-500">
    <div className="bg-emerald-50 py-12">
      <div className="container mx-auto px-4">
        <button onClick={onBack} className="flex items-center text-slate-500 hover:text-emerald-600 mb-6 font-bold text-sm">
          <ArrowLeft size={16} className="mr-1" /> ホームに戻る
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-white rounded-lg shadow-sm text-emerald-600">
            <Car size={32} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">訪問リハビリテーション</h1>
        </div>
        <p className="text-slate-600 max-w-2xl">
          通院が困難な方のご自宅へ理学療法士が訪問し、住み慣れた環境でのリハビリテーションを提供します。
        </p>
      </div>
    </div>

    <div className="container mx-auto px-4 py-16">
      <div className="grid md:grid-cols-2 gap-12 mb-16">
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-l-4 border-emerald-500 pl-4">サービス内容</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              ご本人様やご家族の希望を伺いながら、心身機能の維持・回復、日常生活の自立支援のために、以下のようなプログラムを行います。
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
                <span className="text-emerald-500 font-bold">01</span>
                <span>関節の変形・拘縮の予防と改善</span>
              </li>
              <li className="flex items-start gap-3 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
                <span className="text-emerald-500 font-bold">02</span>
                <span>筋力増強、起き上がり・歩行などの動作練習</span>
              </li>
              <li className="flex items-start gap-3 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
                <span className="text-emerald-500 font-bold">03</span>
                <span>住宅改修のアドバイスや福祉用具の選定</span>
              </li>
              <li className="flex items-start gap-3 bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
                <span className="text-emerald-500 font-bold">04</span>
                <div>
                  <span className="font-bold text-slate-700 block mb-2">閉じこもり防止のために</span>
                  <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                    <li>体力をつけるための散歩や体操指導</li>
                    <li>通いサービスを利用するための準備</li>
                  </ul>
                </div>
              </li>
            </ul>

            {/* 追加コメント：住環境コーディネーターについて */}
            <div className="mt-4 bg-emerald-50 border border-emerald-100 rounded-lg p-4 flex items-start gap-3">
              <div className="bg-white p-2 rounded-full text-emerald-600 shrink-0 shadow-sm">
                <Home size={20} />
              </div>
              <p className="text-sm text-slate-700 leading-relaxed pt-1 font-medium">
                当院には、住環境コーディネーターの資格を取得しているスタッフもいますので、ご自宅の事でお困りの事はご相談下さい。
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-l-4 border-emerald-500 pl-4">ご利用対象者</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2 bg-slate-50 p-6 rounded-lg">
              <li>介護保険で「要支援」「要介護」の認定を受けている方</li>
              <li>主治医が訪問リハビリテーションの必要性を認めた方</li>
              <li>通院が困難な方</li>
            </ul>
          </section>
        </div>

        <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 h-fit">
          <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">ご利用までの流れ</h3>
          <div className="space-y-6 relative">
            <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-slate-200"></div>
            {[
              {
                title: "ご相談",
                desc: "ケアマネージャー、または当院にご相談ください。"
              },
              {
                title: "かかりつけ医(主治医)へのご相談、受診",
                desc: "現在のかかりつけ医にご相談ください。※主治医が当院の場合は不要です。"
              },
              {
                title: "主治医から当院への診療情報提供書",
                desc: "主治医へ、当院宛ての「診療情報提供書」の作成をご依頼ください。"
              },
              {
                title: "当院の訪問リハビリテーション指示医への受診",
                desc: "当院の医師（指示医）による診察を受けていただきます。"
              },
              {
                title: "指示医より訪問リハビリテーションへの指示書の記載",
                desc: "診察に基づき、指示医がリハビリテーション指示書を作成します。"
              },
              {
                title: "リハビリテーションの提供",
                desc: "リハビリ職員がご自宅に伺い、開始となります。"
              }
            ].map((step, i) => (
              <div key={i} className="relative flex gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold z-10 shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h4 className="font-bold text-slate-800">{step.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 pt-8 border-t border-slate-200 text-center">
            <p className="font-bold text-slate-800 mb-2">まずはお気軽にご相談ください</p>
            <p className="text-2xl font-bold text-emerald-600">0866-94-8881</p>
          </div>
        </div>
      </div>

      {/* 法令遵守・運営規定セクション */}
      <section className="mt-16 border-t border-slate-200 pt-16">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <FileText size={24} className="text-slate-500" />
          運営規程・重要事項等のご案内
        </h2>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-100 p-4 border-b border-slate-200 font-bold text-sm text-slate-700">
            事業所概要・運営方針
          </div>
          <div className="p-6 overflow-x-auto">
            <table className="w-full text-sm text-slate-600 border-collapse min-w-[500px]">
              <tbody>
                <tr className="border-b border-slate-100">
                  <th className="py-3 pr-4 text-left font-medium w-1/3 sm:w-1/4 bg-slate-50 pl-2">事業所名</th>
                  <td className="py-3 pl-4">原田整形外科医院 訪問リハビリテーション</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <th className="py-3 pr-4 text-left font-medium bg-slate-50 pl-2">事業所番号</th>
                  <td className="py-3 pl-4">3310810399</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <th className="py-3 pr-4 text-left font-medium bg-slate-50 pl-2">サービス提供地域</th>
                  <td className="py-3 pl-4">総社市全域・岡山市北区高松・倉敷市真備町（一部地域を除く）</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <th className="py-3 pr-4 text-left font-medium bg-slate-50 pl-2">営業日・営業時間</th>
                  <td className="py-3 pl-4">
                    月・火・水・金・土（祝日も実施）<br />
                    9:00〜17:00<br />
                    <span className="text-xs text-slate-500">定休日：木・日、年末年始(12/31・1/1・2・3)</span>
                  </td>
                </tr>
                <tr>
                  <th className="py-3 pr-4 text-left font-medium bg-slate-50 pl-2 align-top">職員体制</th>
                  <td className="py-3 pl-4">
                    <ul className="list-disc list-inside">
                      <li>医師：1名</li>
                      <li>理学療法士：4名</li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="bg-slate-50 p-4 border-t border-slate-200 flex flex-col lg:flex-row justify-end items-end lg:items-center gap-3 flex-wrap">
            {/* 【実装時の注意】
                  実際の運用では、publicフォルダ内の適切な場所（例: /assets/docs/）に
                  PDFファイルを配置し、そのパスをhrefに指定してください。
              */}
            <a
              href="/assets/docs/訪問リハ_運営規程_正式.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold hover:underline"
            >
              <FileText size={14} />
              運営規程（PDF）
            </a>
            <span className="hidden lg:inline text-slate-300">|</span>
            <a
              href="/assets/docs/訪問リハ_重要事項説明書_要介護.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold hover:underline"
            >
              <FileText size={14} />
              重要事項説明書（要介護）（PDF）
            </a>
            <span className="hidden lg:inline text-slate-300">|</span>
            <a
              href="/assets/docs/訪問リハ_重要事項説明書_要支援.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold hover:underline"
            >
              <FileText size={14} />
              重要事項説明書（要支援）（PDF）
            </a>
          </div>
        </div>
      </section>
    </div>
  </div>
);

// 3. オルソ居宅介護支援センター詳細ページ
const CareCenterPage = ({ onBack }) => (
  <div className="animate-in fade-in duration-500">
    <div className="bg-emerald-50 py-12">
      <div className="container mx-auto px-4">
        <button onClick={onBack} className="flex items-center text-slate-500 hover:text-emerald-600 mb-6 font-bold text-sm">
          <ArrowLeft size={16} className="mr-1" /> ホームに戻る
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-white rounded-lg shadow-sm text-emerald-600">
            <ClipboardList size={32} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800">オルソ居宅介護支援センター</h1>
        </div>
        <p className="text-slate-600 max-w-2xl">
          介護に関するお悩み相談の窓口です。ケアマネジャーが、ご本人・ご家族に寄り添ったケアプランを作成します。
        </p>
      </div>
    </div>

    <div className="container mx-auto px-4 py-16">
      <div className="grid md:grid-cols-2 gap-12">
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-l-4 border-emerald-500 pl-4">私たちにできること</h2>
            <p className="text-slate-600 mb-6">
              「介護保険の手続きがわからない」「退院後の生活が不安」「ベッドや手すりを借りたい」など、介護に関するあらゆるご相談に応じます。
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="font-bold text-emerald-700 mb-2">ケアプランの作成</h3>
                <p className="text-sm text-slate-600">利用者様の心身の状態や環境に合わせて、適切なサービスを組み合わせた計画書を作成します。</p>
              </div>
              <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="font-bold text-emerald-700 mb-2">サービス事業者との調整</h3>
                <p className="text-sm text-slate-600">デイサービスや訪問看護、福祉用具など、各種サービス事業者との連絡調整を行います。</p>
              </div>
              <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="font-bold text-emerald-700 mb-2">介護認定の申請代行</h3>
                <p className="text-sm text-slate-600">市役所への要介護認定の申請手続きを、ご本人やご家族に代わって行います。</p>
              </div>
              <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="font-bold text-emerald-700 mb-2">医療機関との連携</h3>
                <p className="text-sm text-slate-600">併設の原田整形外科医院をはじめ、主治医と密に連携を取り、医療と介護の両面からサポートします。</p>
              </div>
            </div>
          </section>
        </div>

        <div className="bg-white p-8 rounded-2xl border-2 border-emerald-100 h-fit">
          <div className="text-center mb-6">
            <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold mb-2 inline-block">無料相談受付中</span>
            <h3 className="text-2xl font-bold text-slate-800">介護のことでお困りではありませんか？</h3>
          </div>
          <div className="bg-slate-50 p-6 rounded-xl mb-6">
            <p className="text-slate-700 text-sm mb-4 leading-relaxed">
              介護保険サービスを利用するには、「ケアプラン（居宅サービス計画）」が必要です。
              当事業所では、経験豊富なケアマネジャー（介護支援専門員）がご相談を承ります。<br />
              <span className="text-emerald-600 font-bold mt-2 block">※ケアプラン作成の費用は全額介護保険から支払われるため、自己負担はありません。</span>
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-2 rounded-full">
                <MapPin size={20} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">所在地</p>
                <p className="font-bold text-slate-700">岡山県総社市井手1208-2<br />医療法人原田整形外科病棟3階</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-2 rounded-full">
                <Phone size={20} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">電話番号（直通）</p>
                <a href="tel:0866946611" className="font-bold text-slate-700 text-xl hover:text-emerald-600 transition-colors">0866-94-6611</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-2 rounded-full">
                <Printer size={20} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">FAX</p>
                <p className="font-bold text-slate-700 text-xl">0866-94-6612</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// 4. お知らせ詳細ページ（新規追加）
const NewsDetailPage = ({ news, onBack, openBlog }) => (
  <div className="animate-in fade-in duration-500">
    <div className="bg-slate-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <button onClick={onBack} className="flex items-center text-slate-500 hover:text-emerald-600 mb-6 font-bold text-sm">
          <ArrowLeft size={16} className="mr-1" /> 一覧に戻る
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-8 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <span className="text-slate-500 font-mono text-sm">{news.date}</span>
              <span className={`${news.categoryColor} text-xs px-3 py-1 rounded-full font-bold`}>{news.category}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 leading-tight">
              {news.title}
            </h1>
          </div>

          <div className="p-8 md:p-12 text-slate-700 leading-relaxed">
            {news.content || <p>記事の内容がありません。</p>}
          </div>

          {news.linkToBlogId && openBlog && (
            <div className="px-8 md:px-12 pb-8 -mt-2">
              <button
                onClick={() => openBlog(BLOG_DATA.find((p) => p.id === news.linkToBlogId))}
                className="inline-flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold py-3 px-5 rounded-lg transition-colors"
              >
                <BookOpen size={18} /> クリニックだよりで詳しく読む <ChevronRight size={16} />
              </button>
            </div>
          )}

          <div className="bg-slate-50 p-8 border-t border-slate-100 text-center">
            <button
              onClick={onBack}
              className="bg-white border border-slate-300 text-slate-600 font-bold py-3 px-8 rounded-lg hover:bg-slate-50 transition-colors"
            >
              お知らせ一覧に戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);


// ブログ一覧ページ
const BlogListPage = ({ posts, openBlog, onBack }) => (
  <div className="animate-in fade-in duration-500">
    <div className="bg-slate-50 py-12 min-h-[60vh]">
      <div className="container mx-auto px-4 max-w-4xl">
        <button onClick={onBack} className="flex items-center text-slate-500 hover:text-emerald-600 mb-6 font-bold text-sm">
          <ArrowLeft size={16} className="mr-1" /> ホームに戻る
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="bg-emerald-500 text-white p-2 rounded-lg">
            <BookOpen size={24} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">クリニックだより</h1>
            <p className="text-sm text-slate-500">健康のお役立ち情報や、当院の取り組みをお届けします</p>
          </div>
        </div>

        <ul className="space-y-4">
          {posts.map((post) => (
            <li key={post.id}>
              <button
                onClick={() => openBlog(post)}
                className="w-full text-left bg-white rounded-xl shadow-sm border border-slate-100 hover:border-emerald-300 hover:shadow-md transition-all p-6 group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-slate-500 font-mono text-sm">{post.date}</span>
                  <span className={`${post.categoryColor} text-xs px-3 py-1 rounded-full font-bold`}>{post.category}</span>
                </div>
                <h2 className="text-lg md:text-xl font-bold text-slate-800 group-hover:text-emerald-600 transition-colors mb-2 leading-snug">
                  {post.title}
                </h2>
                {post.excerpt && (
                  <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                )}
                <span className="inline-flex items-center text-emerald-600 text-sm font-bold mt-3">
                  続きを読む <ChevronRight size={16} className="ml-1" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// ブログ記事ページ
const BlogDetailPage = ({ post, onBack, goHome }) => (
  <div className="animate-in fade-in duration-500">
    <div className="bg-slate-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <button onClick={onBack} className="flex items-center text-slate-500 hover:text-emerald-600 mb-6 font-bold text-sm">
          <ArrowLeft size={16} className="mr-1" /> クリニックだよりに戻る
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-8 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <span className="text-slate-500 font-mono text-sm">{post.date}</span>
              <span className={`${post.categoryColor} text-xs px-3 py-1 rounded-full font-bold`}>{post.category}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 leading-tight">
              {post.title}
            </h1>
          </div>

          <div className="p-8 md:p-12 text-slate-700 leading-relaxed">
            {post.content || <p>記事の内容がありません。</p>}
          </div>

          <div className="bg-slate-50 p-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={onBack}
              className="w-full sm:w-auto bg-white border border-slate-300 text-slate-600 font-bold py-3 px-8 rounded-lg hover:bg-slate-50 transition-colors"
            >
              クリニックだより一覧へ
            </button>
            <button
              onClick={goHome}
              className="w-full sm:w-auto bg-white border border-slate-300 text-slate-600 font-bold py-3 px-8 rounded-lg hover:bg-slate-50 transition-colors"
            >
              ホームに戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);


// 医療・介護関係者の方へ（入院のご紹介）ページ
const MedicalReferralPage = ({ onBack }) => (
  <div className="animate-in fade-in duration-500">
    <div className="bg-slate-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl printable">
        <button onClick={onBack} className="no-print flex items-center text-slate-500 hover:text-emerald-600 mb-6 font-bold text-sm">
          <ArrowLeft size={16} className="mr-1" /> ホームに戻る
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="bg-slate-800 text-white p-2 rounded-lg">
            <Building size={24} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">医療・介護関係者の方へ</h1>
            <p className="text-sm text-slate-500">入院・手術のご紹介について（ケアマネジャー様・医療機関の先生方へ）</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 mb-6 text-slate-700 leading-relaxed">
          <p>
            平素より、地域の医療・介護にご尽力いただきありがとうございます。原田整形外科医院では、ケアマネジャー様・医療機関の先生方からの入院・手術のご紹介を随時お受けしております。「入院が必要だが行き先に迷う」整形外科疾患の患者様がいらっしゃいましたら、お気軽にご相談ください。
          </p>
        </div>

        {/* 当院でお受けできること */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-4">当院でお受けできる主なケース</h2>
          <ul className="space-y-2">
            {[
              '大腿骨頸部骨折などの手術・術後管理',
              '他院で手術を受けられた方の術後管理の引き継ぎ',
              '保存療法目的の入院（圧迫骨折・強い腰痛・膝痛など）',
              'リハビリ目的の入院（自宅復帰前の歩行訓練など）',
              '廃用症候群の方の離床訓練',
              '退院先がお決まりになるまでの退院支援',
              '病床状況に応じたレスパイト入院',
              '腱鞘炎・ばね指・指骨折ピンニングなどの日帰り手術',
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-700">
                <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </div>

        {/* ADL・認知症 */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-4">ADL・認知症</h2>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-slate-700"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> 全介助の方も対応可能</li>
            <li className="flex items-start gap-2 text-slate-700"><CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" /> 認知症の方（BPSDのある方も含め）対応可能</li>
          </ul>
        </div>

        {/* 医療処置 */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-4">医療処置</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-emerald-50/60 rounded-lg border border-emerald-100 p-4">
              <p className="font-bold text-emerald-700 mb-2 flex items-center gap-1"><CheckCircle2 size={16} /> 受けられる</p>
              <ul className="space-y-1 text-sm text-slate-700">
                <li>喀痰吸引（1日数回程度）</li>
                <li>在宅酸素（HOT）の継続</li>
                <li>末梢からの持続点滴</li>
                <li>インスリン注射</li>
                <li>膀胱留置カテーテル管理</li>
                <li>ストーマ管理</li>
                <li>褥瘡処置（デブリ不要レベル）</li>
              </ul>
            </div>
            <div className="bg-rose-50/60 rounded-lg border border-rose-100 p-4">
              <p className="font-bold text-rose-600 mb-2 flex items-center gap-1"><X size={16} /> 受けるのが難しい</p>
              <ul className="space-y-1 text-sm text-slate-700">
                <li>気管切開、人工呼吸器</li>
                <li>中心静脈栄養</li>
                <li>経管栄養（経鼻胃管・胃ろう）</li>
                <li>透析</li>
                <li>麻薬による疼痛管理</li>
                <li>デブリードマンが必要な褥瘡</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 感染症 */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-4">感染症</h2>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">
              <span className="font-bold text-emerald-700">対応可：</span>
              MRSA保菌のみ（症状なし）、結核既往（治療済み）、COVID-19療養終了後など、活動性のないもの
            </p>
            <p className="text-slate-700">
              <span className="font-bold text-rose-600">対応が難しい：</span>
              活動性のMRSA感染、活動性結核、COVID-19療養中、疥癬、ノロウイルス・インフルエンザ等の急性感染症
            </p>
          </div>
        </div>

        {/* ご紹介の流れ */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-4">ご紹介の流れ</h2>
          <ol className="space-y-4">
            {[
              ['まずはご相談ください', 'お電話でざっくりご相談ください。続けて、利用者様の情報をFAXでお送りいただけるとスムーズです。個人情報を含む詳細のやり取りは、お電話・FAXでお願いいたします。'],
              ['事前情報をお寄せください', 'お名前・年齢・性別／現病歴・既往歴・服薬状況／ADL・認知症の状況／ご家族・キーパーソン／紹介の目的／介護度・ケアプラン概要。ケアマネ様が普段お使いの書式で構いません。'],
              ['受け入れ可否のお返事', '通常2〜3営業日以内にお返事いたします。お急ぎの場合は事前にお伝えください。'],
              ['紹介当日にご持参いただくもの', '健康保険証・後期高齢者医療被保険者証／介護保険被保険者証／お薬手帳（または現在の処方内容がわかるもの）。※診療情報提供書のご用意は不要です。'],
            ].map(([title, body], i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <p className="font-bold text-slate-800">{title}</p>
                  <p className="text-sm text-slate-600 mt-0.5">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* ご相談先 */}
        <div className="bg-slate-800 text-slate-100 rounded-xl p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold text-white mb-1">ご相談先</h2>
          <p className="text-slate-400 text-sm mb-5">お電話・FAXでご一報ください。紹介状は後日でも構いません。</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="bg-slate-700/40 rounded-lg p-5">
              <p className="font-bold text-white mb-3 flex items-center gap-2"><Building size={16} /> 医療機関の先生方へ</p>
              <div className="space-y-1.5 text-sm">
                <p><span className="text-slate-400 inline-block w-16">TEL</span><span className="font-bold text-white">0866-94-8881</span></p>
                <p><span className="text-slate-400 inline-block w-16">FAX</span>0866-90-0015</p>
                <p className="flex"><span className="text-slate-400 inline-block w-16 shrink-0">受付時間</span><span>平日（木曜を除く）9:00〜18:00<br />土曜 9:00〜11:30</span></p>
              </div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-5">
              <p className="font-bold text-white mb-3 flex items-center gap-2"><HeartHandshake size={16} /> ケアマネジャー様へ</p>
              <div className="space-y-1.5 text-sm">
                <p><span className="text-slate-400 inline-block w-16">担当</span>原田（看護師）</p>
                <p><span className="text-slate-400 inline-block w-16">TEL</span><span className="font-bold text-white">0866-94-8881</span></p>
                <p><span className="text-slate-400 inline-block w-16">FAX</span>0866-90-0015</p>
                <p className="flex"><span className="text-slate-400 inline-block w-16 shrink-0">受付時間</span><span>平日・土曜 9:30〜18:00</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* ご案内（PDF） */}
        <div className="no-print bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8">
          <h2 className="text-lg font-bold text-slate-800 border-l-4 border-emerald-400 pl-3 mb-4">ご案内（PDF）</h2>
          <p className="text-sm text-slate-500 mb-4">印刷・保存・FAX用のご案内は、こちらからダウンロードいただけます。</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="/pdf/referral-caremanager.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-slate-50 hover:bg-emerald-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-700 font-bold text-sm transition-colors">
              <FileText size={18} className="text-emerald-600" /> ケアマネジャー様向け ご案内
            </a>
            <a href="/pdf/referral-doctor.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-slate-50 hover:bg-emerald-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-700 font-bold text-sm transition-colors">
              <FileText size={18} className="text-emerald-600" /> 医療機関の先生方向け ご案内
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
);


// --- メインアプリケーション ---

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'rehab', 'care', 'news_detail', 'blog', 'blog_detail', 'referral'
  const [selectedNews, setSelectedNews] = useState(null); // 選択されたニュース記事
  const [selectedBlog, setSelectedBlog] = useState(null); // 選択されたブログ記事
  const [formStatus, setFormStatus] = useState('idle');

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // ページ切り替え関数
  const navigateTo = (pageId) => {
    setCurrentPage(pageId);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // ニュースを開く関数
  const openNews = (newsItem) => {
    setSelectedNews(newsItem);
    navigateTo('news_detail');
  };

  // ブログ記事を開く関数
  const openBlog = (blogItem) => {
    setSelectedBlog(blogItem);
    navigateTo('blog_detail');
  };

  // フォーム送信処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.target;
    const formData = new FormData(form);

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });

      setFormStatus('success');
      form.reset(); // フォームの中身をクリア
    } catch (err) {
      console.error(err);
      setFormStatus('error');
    }
  };

  return (
    <div className="font-sans text-slate-700 bg-slate-50 min-h-screen">
      <Header
        onNavigate={navigateTo}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        toggleMenu={toggleMenu}
      />

      {/* ページ切り替えロジック */}
      {currentPage === 'home' && (
        <HomePageContent
          navigateTo={navigateTo}
          openNews={openNews}
          openBlog={openBlog}
          formStatus={formStatus}
          handleSubmit={handleSubmit}
        />
      )}
      {currentPage === 'rehab' && <VisitingRehabPage onBack={() => navigateTo('home')} />}
      {currentPage === 'care' && <CareCenterPage onBack={() => navigateTo('home')} />}
      {currentPage === 'news_detail' && selectedNews && (
        <NewsDetailPage
          news={selectedNews}
          onBack={() => navigateTo('home')}
          openBlog={openBlog}
        />
      )}
      {currentPage === 'blog' && (
        <BlogListPage
          posts={BLOG_DATA}
          openBlog={openBlog}
          onBack={() => navigateTo('home')}
        />
      )}
      {currentPage === 'blog_detail' && selectedBlog && (
        <BlogDetailPage
          post={selectedBlog}
          onBack={() => navigateTo('blog')}
          goHome={() => navigateTo('home')}
        />
      )}
      {currentPage === 'referral' && <MedicalReferralPage onBack={() => navigateTo('home')} />}

      <Footer onNavigate={navigateTo} />
    </div>
  );
}